import { apiClient } from './client';
import { Tariff, TemplateVersion, DocuSignTemplate, VariantType, UIVariantType } from '../types';

// Tariff API
export const tariffApi = {
  listTariffs: async (): Promise<{ tariffs: Tariff[] }> => {
    return apiClient.get<{ tariffs: Tariff[] }>('/tariffs');
  },

  getTariff: async (tariffId: number): Promise<{ tariff: Tariff; templates: TemplateVersion[] }> => {
    return apiClient.get<{ tariff: Tariff; templates: TemplateVersion[] }>(`/tariffs/${tariffId}`);
  },

  updateTariff: async (
    tariffId: number,
    updates: { automationProviderSettings?: { templateId: Record<string, string> }; automationProvider?: string; PrintOffsets?: object | null }
  ): Promise<{ success: boolean }> => {
    return apiClient.put<{ success: boolean }>(`/tariffs/${tariffId}`, updates);
  },

  getAvailableTariffs: async (): Promise<{ tariffs: Array<{ id: string; title: string; vendor: string; isActive: boolean; enabled: boolean; aliasTariff: { id: string; title: string; vendor: string } | null }> }> => {
    return apiClient.get<{ tariffs: Array<{ id: string; title: string; vendor: string; isActive: boolean; enabled: boolean; aliasTariff: { id: string; title: string; vendor: string } | null }> }>('/tariffs/available');
  },

  createTariff: async (data: {
    id: number;
    TARIF: string;
    VERSICHERUNG: string;
    thanksPage: string;
    automationProvider: string;
    automationProviderSettings: { templateId: Record<string, string> };
  }): Promise<{ tariff: Tariff }> => {
    return apiClient.post<{ tariff: Tariff }>('/tariffs', data);
  },
};

// Template API
export const templateApi = {
  getTemplate: async (templateId: string, version?: number): Promise<{ template: DocuSignTemplate }> => {
    const url = version 
      ? `/templates/${templateId}?version=${version}`
      : `/templates/${templateId}`;
    return apiClient.get<{ template: DocuSignTemplate }>(url);
  },

  saveTemplate: async (
    tariffId: number,
    variantType: VariantType,
    template: DocuSignTemplate,
    userEmail: string
  ): Promise<{ templateId: string; version: number }> => {
    return apiClient.put<{ templateId: string; version: number }>('/templates', {
      tariffId,
      variantType,
      template,
      userEmail,
    });
  },

  listVersions: async (templateId: string): Promise<{ versions: TemplateVersion[] }> => {
    return apiClient.get<{ versions: TemplateVersion[] }>(`/templates/${templateId}/versions`);
  },

  deleteTemplate: async (templateId: string, version: number): Promise<{ success: boolean }> => {
    return apiClient.delete<{ success: boolean }>(`/templates/${templateId}/versions/${version}`);
  },

  getTemplatesByTariffAndVariant: async (
    tariffId: number,
    variantType: VariantType
  ): Promise<{ templates: TemplateVersion[] }> => {
    return apiClient.get<{ templates: TemplateVersion[] }>(
      `/templates/tariff/${tariffId}/variant/${variantType}`
    );
  },

  importFromDocuSign: async (
    tariffId: number,
    variantType: VariantType,
    docusignTemplateId: string,
    userEmail: string
  ): Promise<{ success: boolean; templateId?: string; version?: number; error?: string }> => {
    return apiClient.post<{ success: boolean; templateId?: string; version?: number; error?: string }>(
      '/templates/import',
      { tariffId, variantType, docusignTemplateId, userEmail }
    );
  },

  importFromTariff: async (
    tariffId: number,
    variantType: VariantType,
    sourceTemplateId: string,
    sourceVersion: number,
    userEmail: string
  ): Promise<{ success: boolean; templateId: string; version: number }> => {
    return apiClient.post<{ success: boolean; templateId: string; version: number }>(
      '/templates/import-from-tariff',
      { tariffId, variantType, sourceTemplateId, sourceVersion, userEmail }
    );
  },
};

// Publishing API
export const publishingApi = {
  publishVersion: async (
    templateId: string,
    version: number,
    userEmail: string
  ): Promise<{ success: boolean; publishedVersion: number }> => {
    return apiClient.post<{ success: boolean; publishedVersion: number }>(
      `/templates/${templateId}/publish`,
      { version, userEmail }
    );
  },

  bulkPublish: async (
    tariffId: number,
    versions: Record<UIVariantType, number>,
    userEmail: string
  ): Promise<{ success: boolean; publishedVersions: Record<UIVariantType, number> }> => {
    // Backend accepts 3 UI variants: SAME, DIFFERENT, KIDS
    // KIDS is stored internally as DIFFERENT_EMAIL_KIDS but the publish API uses UI variant names
    return apiClient.post<{ success: boolean; publishedVersions: Record<UIVariantType, number> }>(
      `/templates/tariff/${tariffId}/variant/SAME/publish-all`,
      { versions, userEmail }
    );
  },
};

// Documenso API
export const documensoApi = {
  convert: async (
    templateId: string,
    version: number,
    eventData?: object
  ): Promise<object> => {
    return apiClient.post<object>('/documenso/convert', {
      templateId,
      version,
      eventData,
    });
  },

  submitTest: async (
    templateId: string,
    version: number,
    eventData?: object
  ): Promise<{ envelopeId: string; signingUrl?: string }> => {
    return apiClient.post<{ envelopeId: string; signingUrl?: string }>('/documenso/test', {
      templateId,
      version,
      eventData,
    });
  },
};

